/**
 * DetailsSection Component
 * Displays detailed regulation information (Concept V3 Details tab layout).
 */

import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import dayjs from "dayjs";
import {
  normalizeSynopsisHtml,
  regulationRichHtmlContentSx,
} from "./regulationRichHtmlContentSx";
import {
  buildFusionShellUrl,
  buildRegulationDetailPath,
  NAV_SOURCE_OMIT_DOMAIN,
} from "../../utils/externalNavigation";

interface DetailsSectionProps {
  loading: boolean;
  regulationData: Record<string, unknown> | null;
  /** Backend message when the detail fetch fails */
  fetchErrorMessage?: string | null;
}

const textLabelSx = {
  fontSize: "12px",
  color: "#6A7282",
  mb: "4px",
  display: "block",
} as const;

const metaLabelSx = {
  fontSize: "11px",
  color: "#99A1AF",
  mb: "4px",
  display: "block",
} as const;

const metaValueSx = {
  fontSize: "13px",
  color: "#364153",
  lineHeight: 1.5,
} as const;

interface NamedConceptItem {
  name: string;
  code?: string;
  id?: string;
  is_deleted?: boolean;
}

interface RelatedRegulation {
  regulation_id?: string;
  title?: string;
  name?: string;
  id?: string;
  is_deleted?: boolean;
}

function formatVisibleConceptNames(
  items: NamedConceptItem[] | undefined,
): string {
  const visible = Array.isArray(items)
    ? items.filter((item) => item?.is_deleted !== true)
    : [];
  return visible.length > 0
    ? visible.map((item) => item.name).join(", ")
    : "-";
}

function formatDetailDate(dateValue: unknown): string {
  if (dateValue == null || dateValue === "") return "-";
  try {
    const date = dayjs(String(dateValue));
    if (!date.isValid()) return String(dateValue);
    return date.format("DD-MMM-YYYY");
  } catch {
    return String(dateValue);
  }
}

function MetaField({
  label,
  value,
  title,
  ellipsis,
  gridColumn,
}: {
  label: string;
  value: string;
  title?: string;
  ellipsis?: boolean;
  gridColumn?: string | object;
}) {
  return (
    <Box sx={{ minWidth: 0, gridColumn }}>
      <Typography component="div" sx={metaLabelSx}>
        {label}
      </Typography>
      <Typography
        component="div"
        title={title ?? (ellipsis ? value : undefined)}
        sx={{
          ...metaValueSx,
          ...(ellipsis
            ? {
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }
            : null),
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export const DetailsSection: React.FC<DetailsSectionProps> = ({
  loading,
  regulationData,
  fetchErrorMessage,
}) => {
  if (loading) {
    return (
      <Box sx={{ bgcolor: "background.paper" }}>
        <Typography variant="body2" sx={{ color: "#6b7280" }}>
          Loading regulation details...
        </Typography>
      </Box>
    );
  }

  if (!regulationData) {
    return (
      <Box sx={{ bgcolor: "background.paper" }}>
        <Typography variant="body2" color="error">
          {fetchErrorMessage || "Failed to load regulation details"}
        </Typography>
      </Box>
    );
  }

  const description = regulationData.description as string | undefined;
  const synopsisRaw = regulationData.synopsis as string | undefined;
  const keywords = regulationData.keywords as string | undefined;
  const sourceRaw = regulationData.source as NamedConceptItem[] | undefined;
  const language = regulationData.language as NamedConceptItem[] | undefined;

  const synopsisStr =
    synopsisRaw !== null && synopsisRaw !== undefined
      ? String(synopsisRaw).trim()
      : "";
  const hasSynopsis = synopsisStr.length > 0;
  const synopsisHtml = hasSynopsis ? normalizeSynopsisHtml(synopsisStr) : "";

  const hasDescription = Boolean(
    description !== null &&
      description !== undefined &&
      String(description).trim().length > 0,
  );

  const keywordsList = keywords
    ? keywords
        .split(",")
        .map((k) => k.trim())
        .filter((k) => k.length > 0)
    : [];
  const keywordsDisplay =
    keywordsList.length > 0 ? keywordsList.join(", ") : "-";

  const source = formatVisibleConceptNames(sourceRaw);
  const languages = formatVisibleConceptNames(language);

  const relatedRegulations = (
    (regulationData.related_regulations as RelatedRegulation[]) || []
  ).filter((regulation) => regulation.is_deleted !== true);

  return (
    <Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
        <Box sx={{ mb: 2 }}>
          <Typography component="div" sx={textLabelSx}>
            Synopsis
          </Typography>
          {hasSynopsis ? (
            <Box
              component="div"
              data-testid="regulation-synopsis-html"
              sx={{
                ...regulationRichHtmlContentSx,
                fontSize: "13px",
                color: "#364153",
                lineHeight: 1.6,
              }}
              dangerouslySetInnerHTML={{ __html: synopsisHtml }}
            />
          ) : (
            <Typography sx={metaValueSx}>-</Typography>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography component="div" sx={textLabelSx}>
            Description
          </Typography>
          {hasDescription ? (
            <Box
              component="div"
              data-testid="regulation-description-html"
              sx={{
                ...regulationRichHtmlContentSx,
                fontSize: "13px",
                color: "#364153",
                lineHeight: 1.6,
              }}
              dangerouslySetInnerHTML={{ __html: String(description) }}
            />
          ) : (
            <Typography sx={metaValueSx}>-</Typography>
          )}
        </Box>

        <Box
          data-testid="details-meta-row-primary"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(4, minmax(0, 1fr))",
            },
            gap: 2,
            my: "20px",
          }}
        >
          <MetaField
            label="Keywords"
            value={keywordsDisplay}
            ellipsis
            gridColumn={{ xs: "auto", sm: "span 2" }}
          />
          <MetaField label="Source" value={source || "-"} />
          <MetaField label="Language" value={languages} />
        </Box>

        <Box
          data-testid="details-meta-row-dates"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              sm: "repeat(4, minmax(0, 1fr))",
            },
            gap: 2,
            mb: "20px",
          }}
        >
          <MetaField
            label="Release Date"
            value={formatDetailDate(regulationData.release_date)}
          />
          <MetaField
            label="Publish Date"
            value={formatDetailDate(regulationData.published_date)}
          />
          <MetaField
            label="Coming into Force"
            value={formatDetailDate(regulationData.coming_into_force_date)}
          />
          <MetaField
            label="Effective Date"
            value={formatDetailDate(regulationData.effective_date)}
          />
        </Box>

        <Box>
          <Typography component="div" sx={{ ...textLabelSx, mb: "6px" }}>
            Related Items
          </Typography>
          {relatedRegulations.length === 0 ? (
            <Typography sx={metaValueSx}>-</Typography>
          ) : (
            <Box
              component="ul"
              data-testid="details-related-items"
              sx={{
                m: 0,
                pl: "18px",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                fontSize: "13px",
              }}
            >
              {relatedRegulations.map((regulation, index) => {
                const regulationId =
                  regulation.regulation_id ||
                  regulation.id ||
                  `regulation-${index}`;
                const itemLabel =
                  regulation.name ||
                  regulation.title ||
                  regulationId;
                const detailPath = buildRegulationDetailPath(regulationId, {
                  source: NAV_SOURCE_OMIT_DOMAIN.RELATED_ITEM,
                });
                const isInIframe = window.self !== window.top;
                const href = isInIframe
                  ? buildFusionShellUrl(
                      `/regulation-details/${regulationId}`,
                      {
                        source: NAV_SOURCE_OMIT_DOMAIN.RELATED_ITEM,
                      },
                    )
                  : detailPath;

                return (
                  <Box component="li" key={regulationId}>
                    <Link
                      href={href}
                      target="_blank"
                      rel="noopener"
                      underline="hover"
                      sx={{
                        fontSize: "13px",
                        color: "#2563eb",
                        fontWeight: 500,
                      }}
                    >
                      {itemLabel}
                    </Link>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
